import { useMemo, useState } from "react";
import { PrimaryButton, Table, Textfield } from "widgets";
import ClearIcon from "@mui/icons-material/Clear";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import styles from "./ExportDaySheet.module.scss";
import Loader from "shared/Loader/Loader";
import ErrorButton from "widgets/Buttons/ErrorButton";
import { formatUtcToIstDateTime } from "utils/formatDate";
import {
  useDownloadDaySheet,
  useFetchCenters,
  useFetchDaySheets,
} from "store/backend/assets";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import MultipleSelect from "widgets/Dropdown/MultiSelect";

const INITIAL_FILTERS = {
  centerIds: [],
  startDate: "",
  endDate: "",
  applyFilter: false,
};

const COLUMNS = [
  { label: "Day-Sheet ID", field: "id" },
  { label: "Day-Sheet Close Date", field: "closed_at" },
  { label: "Created By", field: "created_by" },
  { label: "Total Cash Deposit Amount", field: "total_amount" },
  { label: "Download", field: "download" },
];

const FilterField = ({ label, children }) => (
  <div className={styles.filterField}>
    <span className={styles.filterLabel}>{label}</span>
    {children}
  </div>
);

const ExportDaySheet = () => {
  const { openSnackbar } = useSnackbarContext();
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const { data: centers = [], isPending: isCentersLoading } = useFetchCenters();

  const CENTERS = Object.entries(centers || {}).map(([uuid, details]) => ({
    id: details.id,
    value: details.id,
    label: details.name,
  }));
  const { data: daySheets = [], isLoading: isDaySheetsLoading } =
    useFetchDaySheets({ filters });

  const { downloadDaySheet, isLoading: isDownloading } = useDownloadDaySheet();

  const isDateRangeInvalid = useMemo(() => {
    if (!filters.startDate || !filters.endDate) return false;
    return new Date(filters.endDate) <= new Date(filters.startDate);
  }, [filters.startDate, filters.endDate]);

  const canApplyFilters =
    filters.centerIds &&
    filters.startDate &&
    filters.endDate &&
    !isDateRangeInvalid;

  const updateFilter = (key) => (e) => {
    setFilters((prev) => ({
      ...prev,
      [key]: e.target.value,
      applyFilter: false,
    }));
  };

  const clearFilters = () => setFilters(INITIAL_FILTERS);

  const handleDownload = (daySheetId, format) => {
    downloadDaySheet(daySheetId, { ...filters, format })
      .then((res) => {
        const link = document.createElement("a");
        link.href = res.signedUrl;
        link.click();
      })
      .catch((err) => openSnackbar(err.message || "Download failed", "error"));
  };

  const renderDownloadActions = (daySheetId) => (
    <div className={styles.downloadActions}>
      <PrimaryButton
        disabled={isDownloading}
        onClick={() => handleDownload(daySheetId, "PDF")}
      >
        {isDownloading ? "Downloading..." : "Download PDF"}
      </PrimaryButton>

      <PrimaryButton
        disabled={isDownloading}
        onClick={() => handleDownload(daySheetId, "CSV")}
      >
        {isDownloading ? "Downloading..." : "Download CSV"}
      </PrimaryButton>
    </div>
  );

  const rows = useMemo(
    () =>
      daySheets.map((daySheet) => ({
        id: daySheet.id,
        closed_at: formatUtcToIstDateTime(daySheet.close_time),
        created_by: daySheet.created_by,
        total_amount: daySheet.total_amount,
        download: renderDownloadActions(daySheet.id),
      })),
    [daySheets, isDownloading]
  );

  if (isCentersLoading) return <Loader />;

  return (
    <div className={styles.mainContainer}>
      <div className={styles.header}>Export Day-Sheet reports</div>
      <div className={styles.filtersRow}>
        <FilterField label="Center">
          <MultipleSelect
            id="center-select"
            placeholder="Select center"
            value={filters.centerIds}
            menuItems={CENTERS}
            handleChange={updateFilter("centerIds")}
          />
        </FilterField>

        <FilterField label="From (Day-Sheet close date)">
          <Textfield
            type="datetime-local"
            value={filters.startDate}
            onChange={updateFilter("startDate")}
            error={isDateRangeInvalid}
          />
        </FilterField>

        <FilterField label="To (Day-Sheet close date)">
          <Textfield
            type="datetime-local"
            value={filters.endDate}
            onChange={updateFilter("endDate")}
            error={isDateRangeInvalid}
            helperText={
              isDateRangeInvalid ? "To date must be after From date" : ""
            }
          />
        </FilterField>
      </div>
      <div className={styles.filterButtonsRow}>
        <ErrorButton onClick={clearFilters}>
          <ClearIcon fontSize="small" /> Clear Filter
        </ErrorButton>

        <PrimaryButton
          disabled={!canApplyFilters}
          onClick={() => setFilters((prev) => ({ ...prev, applyFilter: true }))}
        >
          <FilterAltIcon fontSize="small" /> Apply Filter
        </PrimaryButton>
      </div>

      {filters.applyFilter && (
        <div className={styles.tableWrapper}>
          {isDaySheetsLoading ? (
            <Loader />
          ) : (
            <Table rows={rows} columns={COLUMNS} />
          )}
        </div>
      )}
    </div>
  );
};

export default ExportDaySheet;
